import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Star, Plus, MousePointer2 } from "lucide-react";

const HERO = {
  name: "Harpeth Ridge Electric",
  cat: "Electrical",
  pin: "#3E8E5A",
  photo: "https://images.unsplash.com/photo-1595211877493-41a4e5f236b3?w=96&h=96&fit=crop&q=80",
};

const EXISTING = [
  { name: "Maple Leaf Plumbing", cat: "Plumbing", pin: "#4A6B82", photo: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=96&h=96&fit=crop&q=80" },
  { name: "Sunlit Window Cleaning", cat: "Cleaning", pin: "#C2734B", photo: "https://images.unsplash.com/photo-1699899657680-421c2c2d5064?w=96&h=96&fit=crop&q=80" },
];

const SIDE_FOLDER = { name: "Lawn & Yard", count: "3 pros", tab: "#7CA0BE", bg: "#DBEAF4" };

const CAPTIONS = [
  "Find a pro you like",
  "Pin them to your board",
  "Create a project folder",
  "Drop them in — sorted!",
];

const MiniCard = ({ p, dragging = false, pinned = false }) => (
  <div className={`relative bg-white rounded-lg p-2.5 ${dragging ? "shadow-[0_18px_34px_rgba(0,0,0,0.32)]" : "shadow-[0_6px_14px_rgba(50,32,14,0.28)]"}`}>
    {pinned && (
      <motion.span
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 420, damping: 12 }}
        className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full shadow"
        style={{ background: `radial-gradient(circle at 35% 30%, #fff, ${p.pin} 60%, rgba(0,0,0,0.5))` }}
      />
    )}
    <div className="flex items-center gap-2">
      <span className="w-8 h-8 rounded-full shrink-0 overflow-hidden ring-2 ring-white bg-[#E1EDE7]">
        <img src={p.photo} alt="" loading="lazy" className="w-full h-full object-cover" />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-bold text-[#2D2926] truncate leading-tight" style={{ fontFamily: "Outfit" }}>{p.name}</p>
        <p className="text-[8px] uppercase tracking-wide text-[#5C554E]">{p.cat}</p>
      </div>
    </div>
    <div className="flex items-center justify-between mt-1.5">
      <span className="inline-flex items-center gap-0.5 text-[#E0A83E]"><Star size={9} fill="currentColor" /><span className="text-[8px] text-[#5C554E]">5.0</span></span>
      {pinned ? (
        <span className="text-[7px] font-semibold text-[#B0446B] bg-[#FBE9F0] rounded-full px-1.5 py-0.5">Pinned</span>
      ) : (
        <span className="text-[7px] font-semibold text-white bg-[#2E6B57] rounded-full px-1.5 py-0.5">Pin</span>
      )}
    </div>
  </div>
);

const StaticFolder = ({ f }) => (
  <div className="relative">
    <span className="absolute top-0 left-2.5 h-2.5 w-9 rounded-t-md z-0" style={{ background: f.tab }} />
    <div className="relative z-10 rounded-lg p-2.5 pt-3 min-h-[56px] flex flex-col justify-end shadow-[0_4px_10px_rgba(50,40,20,0.12)]" style={{ background: f.bg }}>
      <p className="text-[10px] font-bold text-[#2D2926] truncate leading-tight" style={{ fontFamily: "Outfit" }}>{f.name}</p>
      <p className="text-[8px] text-[#5C554E] mt-0.5">{f.count}</p>
    </div>
  </div>
);

export const MyBoardDemo = () => {
  const reduce = useReducedMotion();
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (reduce) { setStep(2); return; }
    const durations = [1800, 2200, 2200, 2600];
    const t = setTimeout(() => setStep((s) => (s + 1) % 4), durations[step]);
    return () => clearTimeout(t);
  }, [step, reduce]);

  const heroAnim =
    step === 0 ? { opacity: 1, x: 6, y: -16, scale: 1.03, rotate: -4 } :
    step === 3 ? { opacity: 0, x: 78, y: 150, scale: 0.3, rotate: 12 } :
    { opacity: 1, x: 0, y: 0, scale: 1, rotate: -1 };

  return (
    <div className="bg-white rounded-2xl shadow-[0_24px_60px_rgba(32,73,59,0.16)] border border-black/5 p-4 sm:p-5 overflow-hidden" data-testid="myboard-demo">
      {/* header + live caption */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-bold text-[#2D2926]" style={{ fontFamily: "Outfit" }}>My Board</p>
        <AnimatePresence mode="wait">
          <motion.span
            key={step}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.25 }}
            className="text-[10px] font-semibold text-[#2E6B57] bg-[#E1EDE7] rounded-full px-2.5 py-1"
            data-testid="myboard-demo-caption"
          >
            {CAPTIONS[step]}
          </motion.span>
        </AnimatePresence>
      </div>

      {/* cork board */}
      <div className="cork-board relative rounded-xl p-4 min-h-[200px] overflow-visible" style={{ background: "radial-gradient(circle at 25% 20%, #c6a06f, #ac8149 55%, #916b3c)" }}>
        <div className="grid grid-cols-2 gap-3.5">
          <MiniCard p={EXISTING[0]} pinned />
          <MiniCard p={EXISTING[1]} pinned />
        </div>

        {/* animated hero card */}
        <motion.div
          className="absolute z-30 w-[46%] left-4 bottom-3"
          initial={{ opacity: 0, y: -46, scale: 0.92, rotate: -6 }}
          animate={heroAnim}
          transition={{ type: "spring", stiffness: 130, damping: 17 }}
        >
          <MiniCard p={HERO} dragging={step === 0} pinned={step === 1 || step === 2} />
        </motion.div>

        {/* cursor hint while dragging */}
        <motion.div
          className="absolute z-40 pointer-events-none text-[#1c3b30]"
          initial={{ opacity: 0 }}
          animate={
            step === 0 ? { opacity: 1, left: "44%", top: "62%" } :
            step === 1 ? { opacity: 1, left: "30%", top: "70%" } :
            { opacity: 0, left: "30%", top: "70%" }
          }
          transition={{ type: "spring", stiffness: 90, damping: 18 }}
        >
          <MousePointer2 size={16} fill="#1c3b30" />
        </motion.div>
      </div>

      {/* project folders */}
      <p className="text-xs font-bold text-[#2D2926] mt-4 mb-3" style={{ fontFamily: "Outfit" }}>Project folders</p>
      <div className="grid grid-cols-3 gap-2.5">
        {/* new project button */}
        <motion.div
          animate={step === 2 ? { scale: [1, 1.06, 1] } : { scale: 1 }}
          transition={{ duration: 0.7, repeat: step === 2 ? Infinity : 0 }}
          className="rounded-lg border-2 border-dashed border-[#B7C9BF] bg-white/60 flex flex-col items-center justify-center gap-1 min-h-[56px] text-[#2E6B57]"
          data-testid="myboard-demo-newproject"
        >
          <Plus size={16} /><span className="text-[8px] font-semibold">New project</span>
        </motion.div>

        {/* new folder slot (appears at step 2, gets the pro at step 3) */}
        <div className="relative">
          <AnimatePresence mode="wait">
            {step >= 2 ? (
              <motion.div
                key="newfolder"
                initial={{ scale: 0, opacity: 0, y: 8 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 300, damping: 18 }}
                className="relative"
                data-testid="myboard-demo-newfolder"
              >
                <AnimatePresence>
                  {step === 3 && (
                    <motion.span
                      key="face"
                      initial={{ scale: 0, y: 6 }}
                      animate={{ scale: 1, y: 0 }}
                      transition={{ type: "spring", stiffness: 400, damping: 14 }}
                      className="absolute -top-2 right-2 z-20 w-5 h-5 rounded-full overflow-hidden ring-2 ring-white bg-[#E1EDE7]"
                    >
                      <img src={HERO.photo} alt="" className="w-full h-full object-cover" />
                    </motion.span>
                  )}
                </AnimatePresence>
                <span className="absolute top-0 left-2.5 h-2.5 w-9 rounded-t-md z-0" style={{ background: "#A38FD1" }} />
                <div className="relative z-10 rounded-lg p-2.5 pt-3 min-h-[56px] flex flex-col justify-end shadow-[0_4px_10px_rgba(50,40,20,0.12)]" style={{ background: "#E4DCF5" }}>
                  <p className="text-[10px] font-bold text-[#2D2926] truncate leading-tight" style={{ fontFamily: "Outfit" }}>Kitchen Remodel</p>
                  <p className="text-[8px] text-[#5C554E] mt-0.5">{step === 3 ? "1 pro" : "New"}</p>
                </div>
              </motion.div>
            ) : (
              <motion.div key="empty" exit={{ opacity: 0 }} className="rounded-lg border-2 border-dashed border-[#CBB9E0]/70 min-h-[56px]" />
            )}
          </AnimatePresence>
        </div>

        {/* existing folder */}
        <StaticFolder f={SIDE_FOLDER} />
      </div>
    </div>
  );
};

export default MyBoardDemo;
